import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

export type TextareaSize = 'sm' | 'md' | 'lg';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  size?: TextareaSize;
  label?: string;
  helperText?: string;
  errorText?: string;
  fullWidth?: boolean;
  required?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      size = 'md',
      label,
      helperText,
      errorText,
      fullWidth = false,
      required = false,
      className,
      id,
      rows = 4,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!errorText;

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-3 text-base',
    };

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {label}
            {required && (
              <span className="ml-1" style={{ color: 'var(--color-error-500)' }}>
                *
              </span>
            )}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          className={cn(
            'w-full rounded-lg border transition-colors focus:outline-none focus:ring-1 resize-vertical',
            sizeClasses[size],
            className
          )}
          style={{
            backgroundColor: 'var(--color-background-paper)',
            borderColor: hasError
              ? 'var(--color-error-500)'
              : 'var(--color-border-light)',
            color: 'var(--color-text-primary)',
            '--tw-ring-color': hasError
              ? 'var(--color-error-500)'
              : 'var(--color-primary-500)',
          } as React.CSSProperties}
          onFocus={(e) => {
            if (!hasError) {
              e.target.style.borderColor = 'var(--color-primary-500)';
            }
            onFocus?.(e);
          }}
          onBlur={(e) => {
            if (!hasError) {
              e.target.style.borderColor = 'var(--color-border-light)';
            }
            onBlur?.(e);
          }}
          aria-invalid={hasError}
          aria-describedby={
            hasError && errorText
              ? `${textareaId}-error`
              : helperText
                ? `${textareaId}-helper`
                : undefined
          }
          {...props}
        />

        {hasError && errorText && (
          <p id={`${textareaId}-error`} className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
            {errorText}
          </p>
        )}

        {!hasError && helperText && (
          <p id={`${textareaId}-helper`} className="mt-1 text-xs opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };

